import { query, where, getDocs } from 'firebase/firestore';
import { db, getTenantCollection } from '../../../firebase/config';
import { getProgramPageUrl, checkAudienceEligibility, getEventEndDateString } from '../../../utils/programUtils';

export function getProgramLink(docSnap, defaultType) {
    const data = docSnap.data();
    return getProgramPageUrl({
        id: docSnap.id,
        type: data.type || defaultType,
        ...data
    });
}

export function sortDocsByOrder(a, b) {
    const dataA = a.data ? a.data() : a;
    const dataB = b.data ? b.data() : b;
    const orderA = dataA?.order !== undefined ? dataA.order : 9999;
    const orderB = dataB?.order !== undefined ? dataB.order : 9999;
    if (orderA !== orderB) return orderA - orderB;
    return (dataA?.title || '').localeCompare(dataB?.title || '');
}

export async function fetchAllProgramSnapshots(canViewPreview) {
    const fetchByType = (t) => getDocs(query(
        getTenantCollection(db, 'programs'),
        where('type', '==', t),
        where('isActive', '==', true)
    ));

    const previewPromise = canViewPreview
        ? getDocs(query(getTenantCollection(db, 'programs'), where('status', '==', 'preview')))
        : Promise.resolve({ docs: [], forEach: () => {}, size: 0 });

    const [seasonalSnapshot, campsSnapshot, racesSnapshot, eventsSnapshot, previewSnapshot] = await Promise.all([
        fetchByType('seasonal'),
        fetchByType('camp'),
        fetchByType('race'),
        fetchByType('event'),
        previewPromise
    ]);

    return { seasonalSnapshot, campsSnapshot, racesSnapshot, eventsSnapshot, previewSnapshot };
}

export function getProgramNavLabel(data, isPreview) {
    const cleanTitle = (data?.title || "").replace(/\s*(\(|\[)Preview(\)|\])/gi, "").trim();
    return isPreview ? `${cleanTitle} [Preview]` : (data?.title || "");
}

function collectAndSort(snapshot, previewDocs, targetType) {
    const liveDocs = [];
    const matchingPreviews = [];
    const replacedBaseIds = new Set();

    if (previewDocs) {
        previewDocs.forEach(doc => {
            const data = doc.data();
            const match = targetType === 'seasonal'
                ? (data.type === 'seasonal' || !data.type)
                : data.type === targetType;
            if (match) {
                matchingPreviews.push(doc);
                if (data.versionOf) {
                    replacedBaseIds.add(data.versionOf);
                }
            }
        });
    }

    snapshot.forEach(doc => {
        // If a preview version exists for this base program, deduplicate by hiding the live base program (Issue #1970)
        if (!replacedBaseIds.has(doc.id)) {
            liveDocs.push(doc);
        }
    });

    const docs = [...liveDocs, ...matchingPreviews];
    docs.sort(sortDocsByOrder);
    return docs;
}

export function transformSeasonalPrograms(seasonalSnapshot, previewSnapshot, canViewPreview) {
    const docs = collectAndSort(seasonalSnapshot, canViewPreview ? previewSnapshot?.docs : null, 'seasonal');
    return docs.map(doc => {
        const data = doc.data();
        const isPreview = data.status === 'preview';
        return {
            id: doc.id,
            link: data.link,
            label: getProgramNavLabel(data, isPreview),
            path: getProgramLink(doc, 'programs'),
            category: data.category || 'core',
            ...(data.versionOf ? { versionOf: data.versionOf } : {})
        };
    });
}

export function transformCampPrograms(campsSnapshot, previewSnapshot, canViewPreview) {
    const docs = collectAndSort(campsSnapshot, canViewPreview ? previewSnapshot?.docs : null, 'camp');
    return docs.map(doc => {
        const data = doc.data();
        const isPreview = data.status === 'preview';
        return {
            id: doc.id,
            link: data.link,
            label: getProgramNavLabel(data, isPreview),
            path: getProgramLink(doc, 'camps'),
            category: data.category || 'training',
            audience: data.audience || 'public',
            parentProgramIds: Array.isArray(data.parentProgramIds) ? data.parentProgramIds : [],
            ...(data.versionOf ? { versionOf: data.versionOf } : {})
        };
    });
}

export function transformRacePrograms(racesSnapshot, previewSnapshot, canViewPreview) {
    const docs = collectAndSort(racesSnapshot, canViewPreview ? previewSnapshot?.docs : null, 'race');
    return docs.map(doc => {
        const data = doc.data();
        const isPreview = data.status === 'preview';
        return {
            id: doc.id,
            link: data.link,
            label: getProgramNavLabel(data, isPreview),
            path: getProgramLink(doc, 'races'),
            ...(data.versionOf ? { versionOf: data.versionOf } : {})
        };
    });
}

export function transformEventPrograms(eventsSnapshot, previewSnapshot, canViewPreview, todayStr = new Date().toLocaleDateString('en-CA')) {
    const docs = collectAndSort(eventsSnapshot, canViewPreview ? previewSnapshot?.docs : null, 'event');
    return docs
        .filter(doc => {
            const data = doc.data();
            const endDateStr = getEventEndDateString(data);
            if (!endDateStr) return true;
            return endDateStr >= todayStr;
        })
        .map(doc => {
            const data = doc.data();
            const isPreview = data.status === 'preview';
            return {
                id: doc.id,
                link: data.link,
                label: getProgramNavLabel(data, isPreview),
                path: getProgramLink(doc, 'events'),
                audience: data.audience || 'public',
                parentProgramIds: Array.isArray(data.parentProgramIds) ? data.parentProgramIds : [],
                ...(data.versionOf ? { versionOf: data.versionOf } : {})
            };
        });
}

export function buildProgramsSubmenu(seasonalPrograms, eventPrograms) {
    const growth = seasonalPrograms.filter(p => p.category === 'growth');
    const core = seasonalPrograms.filter(p => p.category !== 'growth');

    const combinedSubmenu = [];
    if (growth.length > 0) {
        combinedSubmenu.push({ isDivider: true, label: 'Growth Programs', path: '/programs' });
        combinedSubmenu.push(...growth);
    }
    if (core.length > 0) {
        combinedSubmenu.push({ isDivider: true, label: 'Core Programs', path: '/programs' });
        combinedSubmenu.push(...core);
    }
    if (eventPrograms.length > 0) {
        combinedSubmenu.push({ isDivider: true, label: 'Events', path: '/events' });
        combinedSubmenu.push(...eventPrograms);
    }

    return combinedSubmenu;
}

export function buildCampsAndRacesSubmenu(campPrograms, activeRaces) {
    const dreamCamps = campPrograms.filter(c => c.category === 'dream');
    const trainingCamps = campPrograms.filter(c => c.category !== 'dream');
    const hasCamps = campPrograms.length > 0;
    const hasRaces = activeRaces.length > 0;

    const combinedSubmenu = [];
    if (dreamCamps.length > 0) {
        combinedSubmenu.push({ isDivider: true, label: 'Dream Camps', path: '/camps' });
        combinedSubmenu.push(...dreamCamps);
    }
    if (trainingCamps.length > 0) {
        combinedSubmenu.push({ isDivider: true, label: 'Training Camps', path: '/camps' });
        combinedSubmenu.push(...trainingCamps);
    }

    if (hasRaces) {
        combinedSubmenu.push({ isDivider: true, label: 'Races', path: '/races' });
        combinedSubmenu.push(...activeRaces);
    } else if (!hasCamps) {
        combinedSubmenu.push({ isDivider: true, label: 'Races', path: '/races' });
        combinedSubmenu.push({ label: 'No upcoming races', path: null, disabled: true });
    } else {
        combinedSubmenu.push({ isDivider: true, label: 'Races', path: '/races' });
        combinedSubmenu.push({ label: 'No upcoming races', path: null, disabled: true });
    }

    return combinedSubmenu;
}

export function isNavSubItemVisible(subItem, context) {
    const { user, isCommunityMember, isAdmin, registeredProgramIds } = context;

    if (subItem.requireCommunity) {
        if (!user || (!isCommunityMember && !isAdmin)) {
            return false;
        }
    }

    if (subItem.audience && subItem.audience !== 'public') {
        let enrolledIds = [];
        if (registeredProgramIds instanceof Set) {
            enrolledIds = Array.from(registeredProgramIds);
        } else if (Array.isArray(registeredProgramIds)) {
            enrolledIds = registeredProgramIds;
        }

        let userRole = 'visitor';
        if (isAdmin) {
            userRole = 'admin';
        } else if (isCommunityMember) {
            userRole = 'guardian';
        }

        const eligibility = checkAudienceEligibility(subItem, {
            user,
            userAccount: {
                role: userRole,
                isCommunityMember: isCommunityMember ? 'Yes' : 'No'
            },
            enrolledProgramIds: enrolledIds
        });
        if (!eligibility.eligible) {
            return false;
        }
    }

    return true;
}
